import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorCount: this.state.errorCount + 1
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset the error boundary state
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  }

  render() {
    if (this.state.hasError) {
      // Check if too many errors (possible infinite loop)
      if (this.state.errorCount > 3) {
        return (
          <div className="flex justify-center items-center min-h-screen bg-gray-50">
            <Card className="shadow-lg border-0 max-w-md">
              <CardContent className="p-8">
                <div className="flex flex-col items-center space-y-4 text-center">
                  <div className="text-red-500">
                    <AlertTriangle className="w-16 h-16" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Critical Error</h1>
                  <p className="text-gray-600">
                    The application is experiencing repeated errors. Please refresh the page or contact support.
                  </p>
                  <div className="flex space-x-3">
                    <Button 
                      onClick={() => window.location.reload()} 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh Page
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      }

      // Regular error display
      return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <Card className="shadow-lg border-0 max-w-md">
            <CardContent className="p-8">
              <div className="flex flex-col items-center space-y-4 text-center">
                <div className="text-red-500">
                  <AlertTriangle className="w-16 h-16" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
                <p className="text-gray-600">
                  The application encountered an unexpected error. Don't worry, your data is safe.
                </p>
                
                {/* Show error details in development */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="w-full text-left">
                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                      Error details (development only)
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </details>
                )}

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full">
                  <Button 
                    onClick={this.handleReset}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                  >
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/'}
                    variant="outline"
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;